import { type MaybeRefOrGetter, ref, toValue } from "vue";
import { API } from "@/api/api";
import i18n from "@/plugins/i18n";
import { Response } from "@/api/client";
import { useError } from "@/composables/useError";
import { useFilters } from "@/composables/useFilters";
import {
  EDITABLE_ORDER_PAYMENT_STATUSES,
  NON_SELECTABLE_ORDER_PAYMENT_TYPES,
  ORDER_PAYMENT_DEDUCTION_TYPES,
  ORDER_STATUSES_DISCARDING_UNSAVED_PAYMENTS,
  ORDER_STATUSES_WITHOUT_NEW_PAYMENTS,
  OrderPaymentType,
  OrderStatus,
  TONU_ORDER_STATUSES,
} from "@/constants/order";
import { MandatoryPaymentTypes, TravelOrderPaymentStatus, TravelOrderPaymentType } from "@/constants/travelOrder";
import {
  DRY_RUN_TRAVEL_ORDER_STATUSES,
  TRAVEL_ORDER_STATUSES_WITHOUT_ACTIVE_PAYMENTS,
  TravelOrderStatus,
} from "@/constants/trip";
import { generateId } from "@/utils/generateId";
import { capitalize } from "@/utils/string";
import type { CreateOrderPayment, OrderPayment, UpdateOrderPayment } from "@/api/orders/dto/order.dto";
import type { TravelOrderPayment } from "@/api/trips/dto/trip.dto";
import type { DisableRule } from "@/types/validation";
import type { VuetifyRule } from "@/types/props";

const t = i18n.global.t;

export function useOrderPayments() {
  const api = new API();
  const { showAPIError } = useError();
  const { money } = useFilters();

  const isLoading = ref(false);

  function createOrderPaymentDraft(type: string | null = null, rate: number | null = null): OrderPayment {
    return { tempId: generateId(), type, rate, isActive: true };
  }

  function isDeductionPaymentType(type: string | null) {
    return ORDER_PAYMENT_DEDUCTION_TYPES.some((deductionType) => deductionType === type);
  }

  function getMandatoryOrderPaymentType(status?: OrderStatus): OrderPaymentType {
    if (status && TONU_ORDER_STATUSES.includes(status)) {
      return OrderPaymentType.Tonu;
    }

    return OrderPaymentType.FlatLinehaul;
  }

  function createMandatoryOrderPayment(payments: OrderPayment[], status?: OrderStatus): OrderPayment[] {
    const mandatoryType = getMandatoryOrderPaymentType(status);
    const isUnsavedPaymentDiscarded =
      status !== undefined && ORDER_STATUSES_DISCARDING_UNSAVED_PAYMENTS.includes(status);

    const preparedPayments = payments
      .filter((payment) => {
        if (payment.id) {
          return true;
        }

        if (isUnsavedPaymentDiscarded && payment.type !== mandatoryType) {
          return false;
        }

        return !isOtherMandatoryPayment(payment, mandatoryType);
      })
      .map((payment) => {
        if (isOtherMandatoryPayment(payment, mandatoryType)) {
          return { ...payment, isActive: false };
        }

        if (payment.type !== mandatoryType) {
          return { ...payment, isActive: status !== OrderStatus.noTonu };
        }

        return { ...payment, isDeleted: false, isActive: status !== OrderStatus.noTonu };
      });

    if (preparedPayments.some((payment) => payment.type === mandatoryType)) {
      return preparedPayments;
    }

    const rate = status === OrderStatus.waitingTonu || status === OrderStatus.noTonu ? 0 : null;

    return [
      ...preparedPayments,
      { ...createOrderPaymentDraft(mandatoryType, rate), isActive: status !== OrderStatus.noTonu },
    ];
  }

  function isOrderPaymentVisible(payment: OrderPayment, status?: OrderStatus): boolean {
    const visibleOrderStatus = [OrderStatus.noTonu, OrderStatus.cancelled];
    return payment.isActive || visibleOrderStatus.includes(status!);
  }

  function isOtherMandatoryPayment(payment: OrderPayment, mandatoryType: OrderPaymentType): boolean {
    return payment.type !== mandatoryType && NON_SELECTABLE_ORDER_PAYMENT_TYPES.includes(payment.type ?? "");
  }

  function getMandatoryTravelOrderPaymentType(status: TravelOrderStatus): TravelOrderPaymentType {
    if (DRY_RUN_TRAVEL_ORDER_STATUSES.includes(status)) {
      return TravelOrderPaymentType.DryRun;
    }

    return TravelOrderPaymentType.FlatLinehaul;
  }

  function createMandatoryTravelOrderPayment(
    payments: TravelOrderPayment[],
    status: TravelOrderStatus,
  ): TravelOrderPayment[] {
    if (TRAVEL_ORDER_STATUSES_WITHOUT_ACTIVE_PAYMENTS.includes(status)) {
      return deactivateTravelOrderPayments(payments);
    }

    const mandatoryType = getMandatoryTravelOrderPaymentType(status);

    const preparedPayments = payments
      .filter((payment) => payment.id || !isOtherMandatoryTravelOrderPayment(payment, mandatoryType))
      .map((payment) => {
        if (isOtherMandatoryTravelOrderPayment(payment, mandatoryType)) {
          return { ...payment, isActive: false };
        }

        if (payment.type !== mandatoryType) {
          return payment;
        }

        return { ...payment, isDeleted: false, isActive: true };
      });

    if (preparedPayments.some((payment) => payment.type === mandatoryType)) {
      return preparedPayments;
    }

    return [
      ...preparedPayments,
      {
        type: mandatoryType,
        amount: null,
        status: TravelOrderPaymentStatus.New,
        isActive: true,
        tempId: generateId(),
      },
    ];
  }

  function isOtherMandatoryTravelOrderPayment(
    payment: TravelOrderPayment,
    mandatoryType: TravelOrderPaymentType,
  ): boolean {
    return payment.type !== mandatoryType && payment.type !== null && MandatoryPaymentTypes.includes(payment.type);
  }

  function deactivateTravelOrderPayments(payments: TravelOrderPayment[]): TravelOrderPayment[] {
    return payments.filter((payment) => payment.id).map((payment) => ({ ...payment, isActive: false }));
  }

  function getPositiveRateRules(type: string | null, status?: OrderStatus | TravelOrderStatus): VuetifyRule[] {
    if (type === OrderPaymentType.Tonu && status !== OrderStatus.tonu) {
      return [];
    }

    const messages: Record<string, string> = {
      [OrderPaymentType.Tonu]: t("payments.tonu-rate-above-zero"),
      [TravelOrderPaymentType.DryRun]: t("payments.dry-run-rate-above-zero"),
    };
    const message = type ? messages[type] : undefined;

    if (!message) {
      return [];
    }

    return [(value: unknown) => Number(value) > 0 || message];
  }

  function createAddOrderPaymentDisableRules(orderStatus: MaybeRefOrGetter<OrderStatus | undefined>): DisableRule[] {
    const status = toValue(orderStatus);

    return [
      {
        disabled: status !== undefined && ORDER_STATUSES_WITHOUT_NEW_PAYMENTS.includes(status),
        reasons: [t("order-payments.add-payment-unavailable")],
      },
    ];
  }

  function createChangeOrderPaymentDisableRules(payment: MaybeRefOrGetter<OrderPayment>): DisableRule[] {
    const { status } = toValue(payment);

    return [
      {
        disabled: status !== undefined && !EDITABLE_ORDER_PAYMENT_STATUSES.includes(status),
        reasons: [t("order-payments.change-payment-unavailable", { status: capitalize(status ?? "") })],
      },
    ];
  }

  async function createOrderPayment(orderId: string, payment: CreateOrderPayment): Promise<OrderPayment | null> {
    isLoading.value = true;

    try {
      const response = await api.orders.createOrderPayment(orderId, payment);

      if (!response.success || !response.data) {
        showAPIError(response, t("order-payments.create-error"));
        return null;
      }

      return response.data;
    } catch (error) {
      console.error(error);
      showAPIError(Response.unknownError(), t("order-payments.create-error"));
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateOrderPayment(
    orderId: string,
    paymentId: number,
    payment: UpdateOrderPayment,
  ): Promise<OrderPayment | null> {
    isLoading.value = true;

    try {
      const response = await api.orders.updateOrderPayment(orderId, paymentId, payment);

      if (!response.success || !response.data) {
        showAPIError(response, t("order-payments.update-error"));
        return null;
      }

      return response.data;
    } catch (error) {
      console.error(error);
      showAPIError(Response.unknownError(), t("order-payments.update-error"));
      return null;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteOrderPayment(orderId: string, paymentId: number): Promise<boolean> {
    isLoading.value = true;

    try {
      const response = await api.orders.deleteOrderPayment(orderId, paymentId);

      if (!response.success) {
        showAPIError(response, t("order-payments.delete-error"));
        return false;
      }

      return true;
    } catch (error) {
      console.error(error);
      showAPIError(Response.unknownError(), t("order-payments.delete-error"));
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  function formatOrderPaymentRate(rate: number): string {
    return money(rate);
  }

  return {
    isLoading,
    deleteOrderPayment,
    formatOrderPaymentRate,
    updateOrderPayment,
    createOrderPayment,
    createAddOrderPaymentDisableRules,
    createChangeOrderPaymentDisableRules,
    createOrderPaymentDraft,
    isDeductionPaymentType,
    getMandatoryOrderPaymentType,
    createMandatoryOrderPayment,
    isOrderPaymentVisible,
    getPositiveRateRules,
    createMandatoryTravelOrderPayment,
  };
}
